/* eslint-disable max-lines */
import { useCombobox, UseComboboxStateChange } from "downshift";
import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useInfiniteScroll } from "../../../../hooks/useInfiniteScroll";
import { foldSearchText } from "../../../../utils/searchText";
import { IAxiosResponse } from "../../../../interfaces/IAxiosResponse";
import { IPagination } from "../../../../interfaces/IPagination";
import { IList, ISelectHookAsync, ISelectHookOptions } from "../Select.types";
import { instanceOfBaseType } from "../utils";
type handleParams<T> = {
  search?: string;
  prev: boolean;
  params?: T;
};
// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
const useSelect = <T, D extends unknown>({
  asyncFunc,
  emptyItem,
  pageSize,
  options,
  searchFunc,
  searchQuerySelector,
  itemToString,
  dataSelector = "result",
  extraParams,
  disabled,
  searchTermTransform
}: ISelectHookAsync<T, D> | ISelectHookOptions<T, D>) => {
  const [items, setItems] = useState<IList<T>["items"]>([]);
  const [totalPages, setTotalpages] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const _asyncFunc = (params: D): Promise<IAxiosResponse<IPagination<T>>> => {
    if (asyncFunc) return asyncFunc(params);
    else {
      return new Promise((resolve) => {
        resolve({
          status: 200,
          statusText: "200",
          data: { result: [], [dataSelector]: options, totalPages: 1 }
        });
      });
    }
  };
  const { page, resetPage, handleScroll } = useInfiniteScroll({
    loading,
    totalPages
  });

  const _handleFunc = useCallback(
    async ({ search, prev: _prev, params }: handleParams<D>) => {
      setLoading(true);
      // Lets a consumer send something narrower than what was typed — the line
      // pickers drop the "X - Y" separator so the API returns both directions
      // and the client decides which to keep.
      const requestTerm = searchTermTransform
        ? searchTermTransform(search || "")
        : search || "";
      try {
        const res = await _asyncFunc({
          ...(params && params),
          pageSize: pageSize || 40,
          pageIndex: page,
          ...(requestTerm.length
            ? {
                // Pass the raw term; `serialize()` (and axios params) URL-encode
                // it once. Encoding here too caused double-encoding, so accents
                // and spaces (e.g. "Gjakovë") never matched on the backend.
                // Not folded either: the API matches accent-insensitively both
                // ways ("durres" and "durrës" both find "Durrës").
                [searchQuerySelector || "searchKeyword"]: requestTerm
              }
            : {})
        } as D);
        if (res?.status === 200) {
          setItems((prev) => [
            ...(emptyItem && page === 1 ? [emptyItem] : []),
            ...(_prev && page !== 1 ? prev : ([] as IList<T>["items"])),
            ...(res?.data[dataSelector as "result"] || [])
          ]);
        } else setItems([]);
        setTotalpages(res.data.totalPages);
      } catch {
        // A rejected request is a routine outcome, not just an outage:
        // route/linesselectlist answers a zero-match search with a 500. The
        // axios error interceptor re-rejects, so without this the throw escaped
        // and skipped setLoading(false) — the dropdown kept spinning forever
        // over the previous term's results instead of saying "No results".
        // Mid-scroll failures keep the pages already loaded.
        if (_prev && page !== 1) return;
        setItems(emptyItem ? [emptyItem] : []);
        setTotalpages(0);
      } finally {
        setLoading(false);
      }
    },
    [page]
  );
  useEffect(() => {
    if (!disabled) {
      if (extraParams) resetPage();
      void _handleFunc({ prev: true, params: extraParams });
    }
  }, [_handleFunc, disabled, JSON.stringify(extraParams)]);

  const debounceHandleFunc = useMemo(
    () => debounce((params: handleParams<D>) => _handleFunc(params), 1000),
    []
  );
  const _searchFunc = (search?: string) => {
    let condition: boolean = false;
    // Folded on both sides so "sarande" matches "Sarandë" and "cabrat" matches
    // "Çabrat". The async path gets this from the backend's accent-insensitive
    // collation; local `options` had only a lowercase compare, so a typed "e"
    // never reached an "ë".
    const foldedSearch = foldSearchText(search || "");
    setItems(() => [
      ...(emptyItem ? [emptyItem] : []),
      ...(options?.filter((o) => {
        if (instanceOfBaseType(o, "label"))
          condition = foldSearchText(o.label).includes(foldedSearch);
        if (searchFunc) condition = searchFunc(o, search);
        return !search || condition;
      }) || [])
    ]);
  };
  const onInputValueChange = ({
    inputValue,
    ...props
  }: UseComboboxStateChange<T>) => {
    // FunctionSetInputValue covers the inlineSearch clear button, which resets
    // the term programmatically — without it the field would empty while the
    // option list stayed filtered by the old term. No existing consumer calls
    // setInputValue, so nothing else changes behavior here.
    if (
      props.type === useCombobox.stateChangeTypes.InputChange ||
      props.type === useCombobox.stateChangeTypes.FunctionSetInputValue
    ) {
      if (asyncFunc) {
        setLoading(true);
        resetPage();
        void debounceHandleFunc({
          search: inputValue || "",
          prev: false,
          params: extraParams
        });
      } else if (options?.length) _searchFunc(inputValue);
    }
  };

  const _itemToString = (o: T | null) => {
    if (instanceOfBaseType(o, "label")) return o.label || "";
    if (itemToString) return itemToString(o) || "";
    return "";
  };

  return {
    items,
    setItems,
    onInputValueChange,
    handleScroll,
    loading,
    itemToString: _itemToString
  };
};
export default useSelect;
